// Settings page
import { loadSettings, saveSettings } from '../../storage/settings';
import { clearAll } from '../../storage/db';

export function renderSettings(): string {
  const settings = loadSettings();

  return `
    <div class="settings-page">
      <h1 class="page-title">⚙️ Settings</h1>

      <div class="settings-section card mb-md">
        <h3 class="settings-heading">🔊 Suara</h3>
        <div class="setting-row">
          <label>Sound Effects</label>
          <label class="toggle">
            <input type="checkbox" id="setting-sound" ${settings.soundEnabled ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <div class="settings-section card mb-md">
        <h3 class="settings-heading">♟️ Papan Catur</h3>
        <div class="setting-row">
          <label>Warna Papan</label>
          <select id="setting-board-theme" class="setting-select">
            <option value="classic" ${settings.boardTheme === 'classic' ? 'selected' : ''}>Classic (Coklat)</option>
            <option value="ocean" ${settings.boardTheme === 'ocean' ? 'selected' : ''}>Ocean (Biru)</option>
            <option value="forest" ${settings.boardTheme === 'forest' ? 'selected' : ''}>Forest (Hijau)</option>
            <option value="sunset" ${settings.boardTheme === 'sunset' ? 'selected' : ''}>Sunset (Oranye)</option>
            <option value="purple" ${settings.boardTheme === 'purple' ? 'selected' : ''}>Purple (Ungu)</option>
          </select>
        </div>
        <div class="setting-row">
          <label>Gaya Bidak</label>
          <select id="setting-piece-style" class="setting-select">
            <option value="friendly" ${settings.pieceStyle === 'friendly' ? 'selected' : ''}>Friendly (Lucu)</option>
            <option value="classic" ${settings.pieceStyle === 'classic' ? 'selected' : ''}>Classic (Tradisional)</option>
          </select>
        </div>
      </div>

      <div class="settings-section card mb-md">
        <h3 class="settings-heading">🎯 Gameplay</h3>
        <div class="setting-row">
          <label>Tampilkan Langkah Legal</label>
          <label class="toggle">
            <input type="checkbox" id="setting-legal-moves" ${settings.showLegalMoves ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="setting-row">
          <label>Highlight Langkah Terakhir</label>
          <label class="toggle">
            <input type="checkbox" id="setting-last-move" ${settings.showLastMove ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <div class="settings-section card mb-md">
        <h3 class="settings-heading">💾 Data</h3>
        <div class="setting-row">
          <label>Export Data</label>
          <button class="btn btn-ghost btn-sm" id="btn-export">Download JSON</button>
        </div>
        <div class="setting-row">
          <label>Import Data</label>
          <input type="file" id="import-file" accept=".json" style="display:none">
          <button class="btn btn-ghost btn-sm" id="btn-import">Upload JSON</button>
        </div>
        <div class="setting-row">
          <label>Hapus Semua Data</label>
          <button class="btn btn-danger btn-sm" id="btn-clear-data">Reset</button>
        </div>
      </div>

      <div class="settings-section card text-center">
        <p class="text-secondary">ChessKid AI v0.1.0</p>
        <p class="text-secondary" style="font-size:0.8rem">Belajar catur sambil bermain! ♔</p>
      </div>
    </div>
  `;
}

export function initSettings(): void {
  // Sound toggle
  document.getElementById('setting-sound')?.addEventListener('change', (e) => {
    const settings = loadSettings();
    settings.soundEnabled = (e.target as HTMLInputElement).checked;
    saveSettings(settings);
  });

  // Board theme
  document.getElementById('setting-board-theme')?.addEventListener('change', (e) => {
    const settings = loadSettings();
    settings.boardTheme = (e.target as HTMLSelectElement).value;
    saveSettings(settings);
    applyBoardTheme(settings.boardTheme);
  });

  // Piece style
  document.getElementById('setting-piece-style')?.addEventListener('change', (e) => {
    const settings = loadSettings();
    settings.pieceStyle = (e.target as HTMLSelectElement).value;
    saveSettings(settings);
  });

  // Legal moves
  document.getElementById('setting-legal-moves')?.addEventListener('change', (e) => {
    const settings = loadSettings();
    settings.showLegalMoves = (e.target as HTMLInputElement).checked;
    saveSettings(settings);
  });

  // Last move
  document.getElementById('setting-last-move')?.addEventListener('change', (e) => {
    const settings = loadSettings();
    settings.showLastMove = (e.target as HTMLInputElement).checked;
    saveSettings(settings);
  });

  // Export
  document.getElementById('btn-export')?.addEventListener('click', async () => {
    const { getAllKeys, getItem } = await import('../../storage/db');
    const keys = await getAllKeys();
    const data: Record<string, unknown> = {};
    for (const key of keys) {
      data[key.toString()] = await getItem(key.toString());
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chesskid-data.json';
    a.click();
    URL.revokeObjectURL(url);
  });

  // Import
  document.getElementById('btn-import')?.addEventListener('click', () => {
    document.getElementById('import-file')?.click();
  });

  document.getElementById('import-file')?.addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const data = JSON.parse(text);
      const { setItem } = await import('../../storage/db');
      for (const [key, value] of Object.entries(data)) {
        await setItem(key, value);
      }
      alert('Data berhasil diimport! Refresh halaman.');
    } catch {
      alert('File tidak valid!');
    }
  });

  // Clear data
  document.getElementById('btn-clear-data')?.addEventListener('click', async () => {
    if (confirm('Yakin ingin menghapus semua data? Ini tidak bisa dibatalkan!')) {
      await clearAll();
      localStorage.clear();
      alert('Semua data telah dihapus. Refresh halaman.');
    }
  });

  // Apply current theme
  applyBoardTheme(loadSettings().boardTheme);
}

const THEMES: Record<string, { light: string; dark: string }> = {
  classic: { light: '#f0d9b5', dark: '#b58863' },
  ocean: { light: '#dee3e6', dark: '#8ca2ad' },
  forest: { light: '#e8e4c7', dark: '#7a9a6d' },
  sunset: { light: '#f5e6d0', dark: '#d08b5b' },
  purple: { light: '#e8daf0', dark: '#8b6daa' },
};

function applyBoardTheme(theme: string): void {
  const t = THEMES[theme] || THEMES.classic;
  document.documentElement.style.setProperty('--board-light', t.light);
  document.documentElement.style.setProperty('--board-dark', t.dark);
}
